import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Popover,
  Button,
} from "@mui/material";
import {
  Search,
  Visibility,
  Settings,
  Edit,
  Delete,
} from "@mui/icons-material";
import configueService from "pages/configue/service";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
import { getMainPageStatelanguagePrefListRequest } from "pages/configue/slices/language-preferance-slice";
import { useHistory } from "react-router-dom";
import {
  checkFeatureAccess,
  plateformConfigueUser,
} from "utils/role-restriction/helper";

const LanguagePrefDataTable = ({ data }) => {
  // Sample data based on your structure
  const dispatch = useAppDispatch();
  const { mainPageStateLangPrefList, mainPageSearchPaylaod, loading }: any =
    useAppSelector((state) => state.languagePreferanceSlice);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const history = useHistory();

  const { profileData } = useAppSelector((state) => state.gmailLoginReducer);
  const { role: userRoleStr } = profileData;
  const userRole = userRoleStr?.split(",");

  // Delete popup state
  const [deletePopup, setDeletePopup] = useState<any>({
    open: false,
    anchorEl: null,
    selectedRow: null,
  });

  // Filter data based on search term
  const filteredData = data.filter((row) =>
    Object.values(row).some((value) => {
      if (Array.isArray(value)) {
        return value?.some((item) =>
          item?.toString()?.toLowerCase()?.includes(searchTerm.toLowerCase())
        );
      }
      return value
        ?.toString()
        ?.toLowerCase()
        ?.includes(searchTerm.toLowerCase());
    })
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getChipColor = (colorType) => {
    switch (colorType) {
      case "first":
        return "primary";
      case "second":
        return "secondary";
      case "third":
        return "default";
      default:
        return "default";
    }
  };

  const renderLanguageChips = (languages, chipType = "first") => {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 250 }}>
        {languages?.map((language, index) => (
          <Chip
            key={index}
            label={language}
            size="small"
            color={getChipColor(chipType)}
            variant="outlined"
            sx={{ fontSize: "0.75rem" }}
          />
        ))}
      </Box>
    );
  };

  const paginatedData = filteredData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleConfiguration = (row) => {
    console.log("Configure:", row);
    history.push(`/configue/language_preferance/configure/${row?.objectId}`);
  };

  const handleEdit = (row) => {
    history.push(`/configue/language_preferance/update/${row?.objectId}`);
  };

  // Handle delete button click - opens popup
  const handleDeleteClick = (event, row) => {
    setDeletePopup({
      open: true,
      anchorEl: event.currentTarget,
      selectedRow: row,
    });
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      const res: any = await configueService.deleteStateLanguagePreferanceList({
        state_language_preference_id: deletePopup?.selectedRow?.objectId,
      });
      if (res?.status === 200 && res?.data?.status === "success") {
        dispatch(snackbarRequest({ message: res.data.message }));
        dispatch(
          getMainPageStatelanguagePrefListRequest(mainPageSearchPaylaod)
        );
      } else {
        dispatch(snackbarRequest({ message: res.data.message, type: "error" }));
      }
    } catch (error: any) {
      dispatch(
        snackbarRequest({
          message: error?.response?.data?.message,
          type: "error",
        })
      );
    }

    // Close popup
    setDeletePopup({
      open: false,
      anchorEl: null,
      selectedRow: null,
    });
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setDeletePopup({
      open: false,
      anchorEl: null,
      selectedRow: null,
    });
  };

  const hasModificationAccess = checkFeatureAccess(
    userRole,
    plateformConfigueUser?.LANGUAGE_PREFERANCE_READ_WRITE
  );

  return (
    <Box sx={{ width: "100%" }}>
      {/* Table Container with Horizontal Scroll */}
      <TableContainer
        component={Paper}
        elevation={3}
        sx={{
          width: "100%",
          mb: 2,
          maxWidth: "100vw",
          overflowX: "auto",
        }}
      >
        <Table sx={{ minWidth: 900 }} aria-label="educational data table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ minWidth: 150 }}>
                <strong>State</strong>
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                <strong>Board</strong>
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                <strong>Segment</strong>
              </TableCell>
              <TableCell sx={{ minWidth: 100 }}>
                <strong>Class</strong>
              </TableCell>
              <TableCell sx={{ minWidth: 100 }}>
                <strong>Status</strong>
              </TableCell>
              <TableCell sx={{ minWidth: 150 }}>
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData?.map((row, index) => (
              <React.Fragment key={row.objectId}>
                {/* First Row - Basic Info + Actions */}
                <TableRow
                  sx={{
                    "&:hover": { backgroundColor: "#f0f0f0" },
                    opacity: row.isArchived ? 0.6 : 1,
                    borderBottom: "none",
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {row.state_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.board_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.segment_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.class_name}
                      size="small"
                      color="secondary"
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.isArchived ? "Archived" : "Active"}
                      size="small"
                      color={row.isArchived ? "error" : "success"}
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        pointerEvents: hasModificationAccess ? "auto" : "none",
                        cursor: hasModificationAccess
                          ? "pointer"
                          : "not-allowed",
                        opacity: hasModificationAccess ? 1 : 0.6,
                      }}
                    >
                      <Tooltip title="Configuration">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleConfiguration(row)}
                        >
                          <Settings fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleEdit(row)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(event) => handleDeleteClick(event, row)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Second Row - Languages + Created At */}
                <TableRow
                  sx={{
                    "&:hover": { backgroundColor: "#f0f0f0" },
                    opacity: row.isArchived ? 0.6 : 1,
                    backgroundColor: "#fafafa",
                    borderBottom:
                      index === paginatedData.length - 1
                        ? "none"
                        : "2px solid #e0e0e0",
                  }}
                >
                  <TableCell colSpan={4} sx={{ py: 2 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {/* Languages Section */}
                      <Box>
                        {/* <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: "bold", display: "block", mb: 1 }}
                        >
                          Languages:
                        </Typography> */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mb: 0.5 }}
                            >
                              First Languages:
                            </Typography>
                            {renderLanguageChips(row.FirstLanguages, "first")}
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mb: 0.5 }}
                            >
                              Second Languages:
                            </Typography>
                            {renderLanguageChips(row.SecondLanguages, "second")}
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mb: 0.5 }}
                            >
                              Third Languages:
                            </Typography>
                            {renderLanguageChips(row.ThirdLanguages, "third")}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell colSpan={2}>
                    {/* Created At Section */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: "bold" }}
                      >
                        Created At:
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(row.createdAt)}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Popup */}
      <Popover
        open={deletePopup.open}
        anchorEl={deletePopup.anchorEl}
        onClose={handleDeleteCancel}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            p: 2,
            boxShadow: 3,
            borderRadius: 2,
            minWidth: 250,
          },
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body1" sx={{ mb: 1, fontWeight: "medium" }}>
            Are you sure?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Do you want to delete this record?
          </Typography>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDeleteCancel}
              sx={{ minWidth: 80 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={handleDeleteConfirm}
              sx={{ minWidth: 80 }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Pagination */}
      {/* <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {filteredData.length === 0 && searchTerm && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No results found for "{searchTerm}"
          </Typography>
        </Box>
      )} */}
    </Box>
  );
};

export default LanguagePrefDataTable;
